import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Receipt as ReceiptIcon,
  Refresh,
  People,
  School,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  AssignmentInd,
  Stars,
  LocationOn,
  Schedule,
  QrCodeScanner,
  Refresh as RefreshIcon,
  QrCode2,
  Download,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import {
  btApi,
  ChurchManager,
  BTAttendanceRecord,
  BTAttendanceUpdatePayload,
  BTLocation,
  BTClub,
  BTSession,
  TeacherChangeRequest,
  LeadershipEducationProgram,
  SessionTrainingCategory,
  BasicTrainingType,
  BasicTrainingLevel,
  BTClubTrack,
  LeadershipTrack,
} from '../../services/api/btApi';
import { buildAttendanceListParams } from './btAttendanceQuery';
import BTAttendanceEditDialog from './BTAttendanceEditDialog';
import { getAttendanceLocationLabel } from './btAttendanceEdit';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bt-tabpanel-${index}`}
      aria-labelledby={`bt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// BT 이벤트 목록 생성 함수 (여러 년도 지원)
const generateBTEventOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1]; // 전년, 올해, 내년
  const events: { id: string; label: string }[] = [];

  years.forEach(year => {
    events.push(
      { id: `상반기 연합 BT ${year}`, label: `상반기 연합 BT ${year}` },
      { id: `하반기 연합 BT ${year}`, label: `하반기 연합 BT ${year}` },
      { id: `수시 BT ${year}`, label: `수시 BT ${year}` },
    );
  });

  return events;
};

const BT_EVENT_OPTIONS = generateBTEventOptions();

const SESSION_TRAINING_CATEGORY_OPTIONS: Array<{ value: SessionTrainingCategory; label: string }> = [
  { value: 'basic-training', label: 'Basic Training' },
  { value: 'leadership-education', label: '리더십교육' },
];

const BASIC_TRAINING_TYPE_OPTIONS: Array<{ value: BasicTrainingType; label: string }> = [
  { value: 'regular', label: '수시 교육' },
  { value: 'united', label: '연합 교육' },
  { value: 'open-bt', label: 'Open BT' },
];

const BASIC_TRAINING_LEVEL_OPTIONS: Array<{ value: BasicTrainingLevel; label: string }> = [
  { value: 'new', label: '신규' },
  { value: 'renewal', label: '재교육' },
  { value: 'advanced', label: '심화' },
];

const BT_CLUB_TRACK_OPTIONS: Array<{ value: BTClubTrack; label: string }> = [
  { value: 'CUBBIES', label: 'CUBBIES' },
  { value: 'JOURNEY', label: 'JOURNEY' },
  { value: 'PUGGLES', label: 'PUGGLES' },
  { value: 'SPARKS', label: 'SPARKS' },
  { value: 'T&T', label: 'T&T' },
  { value: 'TREK', label: 'TREK' },
  { value: 'SECRETARY', label: '서기' },
  { value: 'COMMON', label: '공통' },
];

const LEADERSHIP_TRACK_OPTIONS: Array<{ value: LeadershipTrack; label: string }> = [
  { value: 'supervisor-school', label: '감독관학교' },
  { value: 'coordinator-school', label: '조정관학교' },
  { value: 'lcs', label: 'LCS(강사후보자 학교)' },
];

const getTrainingCategoryLabel = (category?: SessionTrainingCategory) =>
  SESSION_TRAINING_CATEGORY_OPTIONS.find((option) => option.value === category)?.label || 'Basic Training';

const getBasicTrainingTypeLabel = (type?: BasicTrainingType) =>
  BASIC_TRAINING_TYPE_OPTIONS.find((option) => option.value === type)?.label || '-';

const getBasicTrainingLevelLabel = (level?: BasicTrainingLevel) =>
  BASIC_TRAINING_LEVEL_OPTIONS.find((option) => option.value === level)?.label || '-';

const getLeadershipTrackLabel = (track?: LeadershipTrack) =>
  LEADERSHIP_TRACK_OPTIONS.find((option) => option.value === track)?.label || '-';

const getKoreanChurchName = (churchName?: string) =>
  churchName?.replace(/\s*\([^)]*\)\s*$/, '') || '-';

const getBasicAttendanceCourseLabel = (course?: string) => {
  switch (course) {
    case 'new':
      return '신규';
    case 'renewal':
      return '재교육';
    case 'advanced':
      return '심화';
    default:
      return '-';
  }
};

const getBasicAttendanceProgramLabel = (club: string, course?: string) => {
  const courseLabel = getBasicAttendanceCourseLabel(course);
  return courseLabel === '-' ? club : `${club}-${courseLabel}`;
};

const getTrackDisplayLabel = (track?: BTClubTrack | string | null) => {
  if (!track) return '-';
  if (track === 'SECRETARY') return '서기';
  if (track === 'COMMON') return '공통';
  return track;
};

const getAttendanceBasicProgramLabel = (record: BTAttendanceRecord, clubs: BTClub[]) => {
  const baseClub = getClubNameById(clubs, record.bt_club_id, record.bt_club_name);
  const sessionTrack = record.session?.btClubTrack || record.session?.btClubTracks?.[0];
  const clubLabel = baseClub === '-' && sessionTrack
    ? getTrackDisplayLabel(sessionTrack)
    : baseClub;
  const course = record.education_type || record.session?.basicTrainingLevel;
  return getBasicAttendanceProgramLabel(clubLabel, course);
};

const getAttendanceTrainingCategory = (record: Pick<BTAttendanceRecord, 'sessionId' | 'session_id' | 'session'>): SessionTrainingCategory => {
  const explicitCategory = record.session?.trainingCategory;
  if (explicitCategory === 'basic-training' || explicitCategory === 'leadership-education') {
    return explicitCategory;
  }

  const sessionProgram = record.session?.educationProgram;
  if (sessionProgram) {
    return sessionProgram.startsWith('bt-') ? 'basic-training' : 'leadership-education';
  }

  // 세션 메타데이터가 없으면 기본값을 Basic Training으로 간주한다.
  return 'basic-training';
};

const mapBasicLevelToProgram = (level: BasicTrainingLevel): LeadershipEducationProgram => {
  switch (level) {
    case 'renewal':
      return 'bt-renewal';
    case 'advanced':
      return 'bt-advanced';
    case 'new':
    default:
      return 'bt-new';
  }
};

interface SessionLectureFormItem {
  title: string;
  description: string;
  basicTrainingLevel: BasicTrainingLevel;
  btClubTrack: BTClubTrack;
  btClubTracks: BTClubTrack[];
  leadershipTrack: LeadershipTrack;
}

interface SessionFormData {
  trainingCategory: SessionTrainingCategory;
  basicTrainingType: BasicTrainingType;
  registrationStartDate: string;
  registrationEndDate: string;
  sessionType: 'morning' | 'afternoon' | 'evening' | 'full-day';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  eventId: string;
  lectures: SessionLectureFormItem[];
}

const createDefaultLectureItem = (): SessionLectureFormItem => ({
  title: '',
  description: '',
  basicTrainingLevel: 'new',
  btClubTrack: 'CUBBIES',
  btClubTracks: ['CUBBIES'],
  leadershipTrack: 'supervisor-school',
});

const createDefaultSessionFormData = (eventId: string): SessionFormData => ({
  trainingCategory: 'basic-training',
  basicTrainingType: 'regular',
  registrationStartDate: '',
  registrationEndDate: '',
  sessionType: 'morning',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  maxParticipants: 50,
  eventId,
  lectures: [createDefaultLectureItem()],
});

const getClubLabel = (club?: BTClub | string | null) => {
  if (!club) return '-';
  if (typeof club === 'string') return club;
  return club.bt_club_name || club.club_name || '-';
};

const getClubNameById = (clubs: BTClub[], clubId?: string, fallbackName?: string) => {
  if (!clubId) return fallbackName || '-';
  const matchedClub = clubs.find((club) => club._id === clubId);
  return matchedClub?.club_name || fallbackName || clubId;
};

const getSessionProgramLabel = (program?: string) => {
  switch (program) {
    case 'bt-new':
      return 'BT 신규';
    case 'bt-renewal':
      return 'BT 재교육';
    case 'bt-advanced':
      return 'BT 심화';
    case 'supervisor-school':
      return '감독관학교';
    case 'coordinator-school':
      return '조정관학교';
    case 'lcs':
      return 'LCS(강사후보자 학교)';
    default:
      return '-';
  }
};

const BTAdminPage: React.FC = () => {
  const { role } = useOutletContext<{ role: string }>();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  // 교회 승인 관련 상태
  const [churchManagers, setChurchManagers] = useState<ChurchManager[]>([]);
  const [selectedManager, setSelectedManager] = useState<ChurchManager | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  // 현재 연도의 하반기 BT를 기본값으로 설정
  const currentYear = new Date().getFullYear();
  const defaultEventId = `하반기 연합 BT ${currentYear}`;

  const [approvalData, setApprovalData] = useState({
    eventId: defaultEventId,
    costs: '50000',
    partTeacher: '1',
  });
  const [deletingChurchManagerId, setDeletingChurchManagerId] = useState<string | null>(null);

  // 강의 관리 관련 상태
  const [sessions, setSessions] = useState<BTSession[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<'all' | SessionTrainingCategory>('all');
  const [basicTrainingTypeFilter, setBasicTrainingTypeFilter] = useState<'all' | BasicTrainingType>('all');
  const [basicTrainingLevelFilter, setBasicTrainingLevelFilter] = useState<'all' | BasicTrainingLevel>('all');
  const [clubTrackFilter, setClubTrackFilter] = useState<'all' | BTClubTrack>('all');
  const [sessionDialog, setSessionDialog] = useState(false);
  const [sessionFormData, setSessionFormData] = useState<SessionFormData>(() => createDefaultSessionFormData(defaultEventId));
  const [editingSession, setEditingSession] = useState<BTSession | null>(null);
  const [copyingSession, setCopyingSession] = useState(false);
  const [subLectureDialog, setSubLectureDialog] = useState(false);
  const [selectedParentSession, setSelectedParentSession] = useState<BTSession | null>(null);
  const [attendanceSessionId, setAttendanceSessionId] = useState<string | null>(null);
  const [subLectureAttendance, setSubLectureAttendance] = useState<any[]>([]);
  const [subLectureAttendanceLoading, setSubLectureAttendanceLoading] = useState(false);
  const [applicationSessionId, setApplicationSessionId] = useState<string | null>(null);

  // QR 코드 다이얼로그 상태
  const [qrDialog, setQrDialog] = useState(false);
  const [qrSession, setQrSession] = useState<BTSession | null>(null);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // 출결 관리 관련 상태
  const [attendanceRecords, setAttendanceRecords] = useState<BTAttendanceRecord[]>([]);
  const [locations, setLocations] = useState<BTLocation[]>([]);
  const [attendanceFilterLocations, setAttendanceFilterLocations] = useState<BTLocation[]>([]);
  const [clubs, setClubs] = useState<BTClub[]>([]);
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [filterClub, setFilterClub] = useState<string>('');
  const [attendanceTypeFilter, setAttendanceTypeFilter] = useState<'all' | 'basic-training' | 'leadership-education'>('all');
  const [leadershipSessionFilter, setLeadershipSessionFilter] = useState<string>('');
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceLoadingMore, setAttendanceLoadingMore] = useState(false);
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendanceTotalCount, setAttendanceTotalCount] = useState(0);
  const [hasMoreAttendance, setHasMoreAttendance] = useState(false);
  const attendanceRequestIdRef = useRef(0);
  const [editingAttendance, setEditingAttendance] = useState<BTAttendanceRecord | null>(null);

  // 교사 변경 요청 관련 상태
  const [teacherChangeRequests, setTeacherChangeRequests] = useState<TeacherChangeRequest[]>([]);
  const [changeRequestLoading, setChangeRequestLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TeacherChangeRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // 장소 관리 관련 상태 (hooks must be declared before any conditional returns)
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [newLocation, setNewLocation] = useState({
    location_name: ''
  });

  // 출석 기록 관리 관련 상태 (hooks must be declared before any conditional returns)
  const [btAttendanceRecords, setBtAttendanceRecords] = useState([]);
  const [btAttendanceLoading, setBtAttendanceLoading] = useState(false);

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/admin');
      return;
    }
    fetchChurchManagers();
    fetchSessions();
    fetchLocations();
    fetchAttendanceFilterLocations();
    fetchClubs();
    fetchTeacherChangeRequests();
  }, [role, navigate]);

  // 필터 변경 시 출결 데이터 재조회
  useEffect(() => {
    if (currentTab === 2) {
      fetchAttendanceFilterLocations();
      fetchAttendanceData();
    }
  }, [filterLocation, filterClub, attendanceTypeFilter, leadershipSessionFilter, currentTab]);

  const fetchChurchManagers = async () => {
    try {
      const response = await btApi.getChurchManagers();
      setChurchManagers(response.data);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'BT 신청 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await btApi.getSessions({ page: '1', limit: '500' });
      setSessions(response.data || []);
    } catch (error: any) {
      console.error('세션 목록 조회 오류:', error);
      setSnackbar({
        open: true,
        message: '세션 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    }
  };

  // 출결 데이터 조회
  const fetchAttendanceData = async (page = 1) => {
    const requestId = ++attendanceRequestIdRef.current;
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setAttendanceLoading(true);
    } else {
      setAttendanceLoadingMore(true);
    }
    try {
      const params = buildAttendanceListParams({
        page,
        filterLocation,
        filterClub,
        attendanceTypeFilter,
        leadershipSessionFilter,
        attendanceFilterLocations,
      });
      const response = await btApi.getAttendanceList(params);
      if (requestId !== attendanceRequestIdRef.current) return;

      const records = response.data || [];
      setAttendanceRecords((previous) => isFirstPage ? records : [...previous, ...records]);
      setAttendancePage(page);
      setAttendanceTotalCount(response.meta?.totalCount || 0);
      setHasMoreAttendance(page < (response.meta?.totalPage || 0));
    } catch (error: any) {
      if (requestId !== attendanceRequestIdRef.current) return;

      console.error('출석 데이터 조회 오류:', error);
      setSnackbar({
        open: true,
        message: '출석 데이터를 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      if (requestId === attendanceRequestIdRef.current) {
        setAttendanceLoading(false);
        setAttendanceLoadingMore(false);
      }
    }
  };

  const handleAttendanceTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      hasMoreAttendance &&
      !attendanceLoading &&
      !attendanceLoadingMore &&
      scrollHeight - scrollTop - clientHeight < 120
    ) {
      fetchAttendanceData(attendancePage + 1);
    }
  };

  // 수강지역 목록 조회
  const fetchLocations = async () => {
    try {
      const data = await btApi.getLocations();
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('수강지역 조회 오류:', error);
    }
  };

  // 출결 필터용 수강지역 목록 조회 (삭제된 지역 포함)
  const fetchAttendanceFilterLocations = async () => {
    try {
      const data = await btApi.getLocations({ includeDeleted: 'true' });
      setAttendanceFilterLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('출결 필터 수강지역 조회 오류:', error);
    }
  };

  // 클럽 목록 조회
  const fetchClubs = async () => {
    try {
      const data = await btApi.getClubs();
      setClubs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('클럽 조회 오류:', error);
    }
  };

  // 교사 변경 요청 조회
  const fetchTeacherChangeRequests = async () => {
    setChangeRequestLoading(true);
    try {
      const response = await btApi.getTeacherChangeRequests({ status: 'pending' });
      setTeacherChangeRequests(response.data || []);
    } catch (error) {
      console.error('교사 변경 요청 조회 오류:', error);
    } finally {
      setChangeRequestLoading(false);
    }
  };

  // 교사 변경 요청 승인
  const handleApproveRequest = async (requestId: string) => {
    try {
      await btApi.updateTeacherChangeRequestStatus(requestId, 'approved');
      setSnackbar({
        open: true,
        message: '교사 변경 요청이 승인되었습니다.',
        severity: 'success',
      });
      fetchTeacherChangeRequests();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '승인 처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  // 교사 변경 요청 거절
  const handleRejectRequest = async () => {
    if (!selectedRequest) return;
    try {
      await btApi.updateTeacherChangeRequestStatus(selectedRequest._id, 'rejected', rejectionReason);
      setSnackbar({
        open: true,
        message: '교사 변경 요청이 거절되었습니다.',
        severity: 'success',
      });
      setRejectDialog(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchTeacherChangeRequests();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '거절 처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  // 출석 삭제
  const handleDeleteAttendance = async (id: string) => {
    if (!window.confirm('정말로 이 출석 기록을 삭제하시겠습니까?')) return;

    try {
      await btApi.deleteAttendance(id);
      setSnackbar({
        open: true,
        message: '출석 기록이 삭제되었습니다.',
        severity: 'success',
      });
      fetchAttendanceData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: '출석 삭제에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const handleUpdateAttendance = async (payload: BTAttendanceUpdatePayload) => {
    if (!editingAttendance) return;

    await btApi.updateAttendance(editingAttendance._id, payload);
    setSnackbar({
      open: true,
      message: '출석 기록이 수정되었습니다.',
      severity: 'success',
    });
    setEditingAttendance(null);
    fetchAttendanceData(1);
  };

  const handleDeleteChurchManager = async (manager: ChurchManager) => {
    const churchName = getKoreanChurchName(manager.churchName);
    const confirmed = window.confirm(
      `"${churchName}" 교회 신청 내역을 삭제하시겠습니까?\n삭제된 신청 내역은 복구할 수 없습니다.`
    );
    if (!confirmed) return;

    setDeletingChurchManagerId(manager._id);
    try {
      await btApi.deleteChurchManager(manager._id);
      setSnackbar({
        open: true,
        message: '교회 신청 내역이 삭제되었습니다.',
        severity: 'success',
      });
      await fetchChurchManagers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '교회 신청 내역 삭제에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setDeletingChurchManagerId(null);
    }
  };

  const handleApproval = async (managerId: string, approve: boolean) => {
    try {
      if (approve && selectedManager) {
        await btApi.updateChurchManagerStatus(
          managerId,
          'approved',
          approvalData.eventId,
          parseInt(approvalData.costs),
          parseInt(approvalData.partTeacher)
        );
        
        // 키 생성 요청
        try {
          await fetch('/api/bt/keys/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              churchManagerId: managerId,
              eventId: approvalData.eventId,
              keyCount: parseInt(approvalData.partTeacher),
            }),
          });
        } catch (keyError) {
          console.error('키 생성 오류:', keyError);
        }
        
        setSnackbar({
          open: true,
          message: '승인이 완료되었습니다. 키가 자동으로 생성되었습니다.',
          severity: 'success',
        });
      } else {
        await btApi.updateChurchManagerStatus(managerId, 'rejected');
        setSnackbar({
          open: true,
          message: '신청이 거절되었습니다.',
          severity: 'success',
        });
      }
      
      fetchChurchManagers();
      setApprovalDialog(false);
      setSelectedManager(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  const openApprovalDialog = (manager: ChurchManager) => {
    setSelectedManager(manager);
    setApprovalData({
      eventId: defaultEventId,
      costs: '50000',
      partTeacher: manager.participants?.toString() || '1',
    });
    setApprovalDialog(true);
  };

  const resetSessionForm = () => {
    setSessionFormData(createDefaultSessionFormData(defaultEventId));
  };

  const addLectureItem = () => {
    setSessionFormData((prev) => ({
      ...prev,
      lectures: [...prev.lectures, createDefaultLectureItem()],
    }));
  };

  const removeLectureItem = (index: number) => {
    setSessionFormData((prev) => ({
      ...prev,
      lectures: prev.lectures.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateLectureItem = <K extends keyof SessionLectureFormItem>(index: number, key: K, value: SessionLectureFormItem[K]) => {
    setSessionFormData((prev) => ({
      ...prev,
      lectures: prev.lectures.map((lecture, itemIndex) => {
        if (itemIndex !== index) return lecture;

        if (key === 'btClubTracks') {
          const tracks = value as BTClubTrack[];
          return {
            ...lecture,
            btClubTracks: tracks,
            btClubTrack: tracks[0] || 'CUBBIES',
          };
        }

        return {
          ...lecture,
          [key]: value,
        };
      }),
    }));
  };

  const handleSessionSubmit = async () => {
    try {
      const commonPayload = {
        trainingCategory: sessionFormData.trainingCategory,
        basicTrainingType: sessionFormData.trainingCategory === 'basic-training' ? sessionFormData.basicTrainingType : undefined,
        registrationStartDate: sessionFormData.registrationStartDate,
        registrationEndDate: sessionFormData.registrationEndDate,
        sessionType: sessionFormData.sessionType,
        date: sessionFormData.date,
        startTime: sessionFormData.startTime,
        endTime: sessionFormData.endTime,
        location: sessionFormData.location,
        maxParticipants: sessionFormData.maxParticipants,
        eventId: sessionFormData.eventId,
      };

      const lecturePayloads = sessionFormData.lectures.map((lecture) => {
        if (sessionFormData.trainingCategory === 'basic-training') {
          const educationProgram: LeadershipEducationProgram = mapBasicLevelToProgram(lecture.basicTrainingLevel);
          return {
            ...commonPayload,
            title: lecture.title,
            description: lecture.description,
            basicTrainingLevel: lecture.basicTrainingLevel,
            btClubTrack: lecture.btClubTracks?.[0] || lecture.btClubTrack,
            btClubTracks: lecture.btClubTracks,
            leadershipTrack: undefined,
            educationProgram,
          };
        }

        return {
          ...commonPayload,
          title: lecture.title,
          description: lecture.description,
          basicTrainingLevel: undefined,
          btClubTrack: undefined,
          btClubTracks: undefined,
          leadershipTrack: lecture.leadershipTrack,
          educationProgram: lecture.leadershipTrack,
        };
      });

      if (editingSession) {
        const [firstLecture, ...extraLectures] = lecturePayloads;
        const existingGroupSessions = sessions.filter((candidate) => (
          candidate.eventId === editingSession.eventId
          && candidate.date === editingSession.date
          && candidate.startTime === editingSession.startTime
          && candidate.endTime === editingSession.endTime
          && candidate.location === editingSession.location
          && candidate.trainingCategory === editingSession.trainingCategory
        ));
        await btApi.updateSession(editingSession._id, firstLecture);
        for (let index = 0; index < extraLectures.length; index += 1) {
          const lecturePayload = extraLectures[index];
          const existingSession = existingGroupSessions[index + 1];
          if (existingSession) {
            await btApi.updateSession(existingSession._id, lecturePayload);
          } else {
            await btApi.createSession(lecturePayload);
          }
        }

        setSnackbar({
          open: true,
          message: extraLectures.length > 0
            ? `강의가 수정되고 ${extraLectures.length}개 강의가 추가 생성되었습니다.`
            : '강의가 수정되었습니다.',
          severity: 'success',
        });
      } else {
        for (const lecturePayload of lecturePayloads) {
          await btApi.createSession(lecturePayload);
        }

        setSnackbar({
          open: true,
          message: copyingSession
            ? `새 상위 강의로 하위 강의 ${lecturePayloads.length}개가 복사되었습니다.`
            : lecturePayloads.length > 1
            ? `새 강의 ${lecturePayloads.length}개가 생성되었습니다.`
            : '새 강의가 생성되었습니다.',
          severity: 'success',
        });
      }

      // 세션 목록 새로고침
      fetchSessions();
      setSessionDialog(false);
      setEditingSession(null);
      setCopyingSession(false);
      resetSessionForm();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '강의 처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  const handleEditSession = (session: BTSession) => {
    const groupedSessions = sessions.filter((candidate) => (
      candidate.eventId === session.eventId
      && candidate.date === session.date
      && candidate.startTime === session.startTime
      && candidate.endTime === session.endTime
      && candidate.location === session.location
      && candidate.trainingCategory === session.trainingCategory
    ));
    const inferredCategory: SessionTrainingCategory = session.trainingCategory
      || (session.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');

    const inferredBasicLevel: BasicTrainingLevel = session.basicTrainingLevel
      || (session.educationProgram === 'bt-renewal'
        ? 'renewal'
        : session.educationProgram === 'bt-advanced'
          ? 'advanced'
          : 'new');

    const inferredLeadershipTrack: LeadershipTrack = session.leadershipTrack
      || (session.educationProgram === 'supervisor-school' || session.educationProgram === 'coordinator-school' || session.educationProgram === 'lcs'
        ? session.educationProgram
        : 'supervisor-school');

    setEditingSession(session);
    setCopyingSession(false);
    setSessionFormData({
      trainingCategory: inferredCategory,
      basicTrainingType: session.basicTrainingType || 'regular',
      registrationStartDate: session.registrationStartDate ? session.registrationStartDate.slice(0, 10) : '',
      registrationEndDate: session.registrationEndDate ? session.registrationEndDate.slice(0, 10) : '',
      sessionType: session.sessionType,
      date: session.date ? session.date.slice(0, 10) : '',
      startTime: session.startTime,
      endTime: session.endTime,
      location: session.location,
      maxParticipants: session.maxParticipants,
      eventId: session.eventId,
      lectures: groupedSessions.map((groupedSession) => ({
        title: groupedSession.title,
        description: groupedSession.description,
        basicTrainingLevel: groupedSession.basicTrainingLevel || (
          groupedSession.educationProgram === 'bt-renewal'
            ? 'renewal'
            : groupedSession.educationProgram === 'bt-advanced'
              ? 'advanced'
              : inferredBasicLevel
        ),
        btClubTrack: groupedSession.btClubTrack || 'CUBBIES',
        btClubTracks: groupedSession.btClubTracks && groupedSession.btClubTracks.length > 0
          ? [groupedSession.btClubTracks[0]]
          : [groupedSession.btClubTrack || 'CUBBIES'],
        leadershipTrack: groupedSession.leadershipTrack || inferredLeadershipTrack,
      })),
    });
    setSessionDialog(true);
  };

  const handleCopySession = (session: BTSession) => {
    const groupedSessions = sessions.filter((candidate) => (
      candidate.eventId === session.eventId
      && candidate.date === session.date
      && candidate.startTime === session.startTime
      && candidate.endTime === session.endTime
      && candidate.location === session.location
      && candidate.trainingCategory === session.trainingCategory
    ));
    const inferredCategory: SessionTrainingCategory = session.trainingCategory
      || (session.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');
    const inferredBasicLevel: BasicTrainingLevel = session.basicTrainingLevel
      || (session.educationProgram === 'bt-renewal'
        ? 'renewal'
        : session.educationProgram === 'bt-advanced'
          ? 'advanced'
          : 'new');
    const inferredLeadershipTrack: LeadershipTrack = session.leadershipTrack
      || (session.educationProgram === 'supervisor-school' || session.educationProgram === 'coordinator-school' || session.educationProgram === 'lcs'
        ? session.educationProgram
        : 'supervisor-school');

    setEditingSession(null);
    setCopyingSession(true);
    setSessionFormData({
      trainingCategory: inferredCategory,
      basicTrainingType: session.basicTrainingType || 'regular',
      registrationStartDate: session.registrationStartDate ? session.registrationStartDate.slice(0, 10) : '',
      registrationEndDate: session.registrationEndDate ? session.registrationEndDate.slice(0, 10) : '',
      sessionType: session.sessionType,
      date: session.date ? session.date.slice(0, 10) : '',
      startTime: session.startTime,
      endTime: session.endTime,
      location: session.location,
      maxParticipants: session.maxParticipants,
      eventId: session.eventId,
      lectures: groupedSessions.map((groupedSession) => ({
        title: groupedSession.title,
        description: groupedSession.description,
        basicTrainingLevel: groupedSession.basicTrainingLevel || (
          groupedSession.educationProgram === 'bt-renewal'
            ? 'renewal'
            : groupedSession.educationProgram === 'bt-advanced'
              ? 'advanced'
              : inferredBasicLevel
        ),
        btClubTrack: groupedSession.btClubTrack || 'CUBBIES',
        btClubTracks: groupedSession.btClubTracks && groupedSession.btClubTracks.length > 0
          ? [groupedSession.btClubTracks[0]]
          : [groupedSession.btClubTrack || 'CUBBIES'],
        leadershipTrack: groupedSession.leadershipTrack || inferredLeadershipTrack,
      })),
    });
    setSessionDialog(true);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('정말로 이 강의를 삭제하시겠습니까?')) return;

    try {
      await btApi.deleteSession(sessionId);
      setSnackbar({
        open: true,
        message: '강의가 삭제되었습니다.',
        severity: 'success',
      });
      fetchSessions();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '강의 삭제에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const handleDeleteSessionGroup = async (session: BTSession) => {
    const groupedSessions = sessions.filter((candidate) => (
      candidate.eventId === session.eventId
      && candidate.date === session.date
      && candidate.startTime === session.startTime
      && candidate.endTime === session.endTime
      && candidate.location === session.location
      && candidate.trainingCategory === session.trainingCategory
    ));
    const lectureCount = groupedSessions.length;
    const message = lectureCount > 1
      ? `이 상위 강의의 하위 강의 ${lectureCount}개를 모두 삭제하시겠습니까?`
      : '정말로 이 강의를 삭제하시겠습니까?';

    if (!window.confirm(message)) return;

    try {
      for (const groupedSession of groupedSessions) {
        await btApi.deleteSession(groupedSession._id);
      }
      setSnackbar({
        open: true,
        message: lectureCount > 1
          ? `하위 강의 ${lectureCount}개가 모두 삭제되었습니다.`
          : '강의가 삭제되었습니다.',
        severity: 'success',
      });
      fetchSessions();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '강의 삭제에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const handleAddSubLecture = (parentSession: BTSession) => {
    const inferredCategory: SessionTrainingCategory = parentSession.trainingCategory
      || (parentSession.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');
    const inferredBasicLevel: BasicTrainingLevel = parentSession.basicTrainingLevel
      || (parentSession.educationProgram === 'bt-renewal'
        ? 'renewal'
        : parentSession.educationProgram === 'bt-advanced'
          ? 'advanced'
          : 'new');
    const inferredLeadershipTrack: LeadershipTrack = parentSession.leadershipTrack
      || (parentSession.educationProgram === 'supervisor-school'
        || parentSession.educationProgram === 'coordinator-school'
        || parentSession.educationProgram === 'lcs'
        ? parentSession.educationProgram
        : 'supervisor-school');

    setSubLectureDialog(false);
    setEditingSession(null);
    setSessionFormData({
      trainingCategory: inferredCategory,
      basicTrainingType: parentSession.basicTrainingType || 'regular',
      registrationStartDate: parentSession.registrationStartDate?.slice(0, 10) || '',
      registrationEndDate: parentSession.registrationEndDate?.slice(0, 10) || '',
      sessionType: parentSession.sessionType,
      date: parentSession.date.slice(0, 10),
      startTime: parentSession.startTime,
      endTime: parentSession.endTime,
      location: parentSession.location,
      maxParticipants: parentSession.maxParticipants,
      eventId: parentSession.eventId,
      lectures: [{
        title: '',
        description: '',
        basicTrainingLevel: inferredBasicLevel,
        btClubTrack: parentSession.btClubTrack || 'CUBBIES',
        btClubTracks: [parentSession.btClubTrack || 'CUBBIES'],
        leadershipTrack: inferredLeadershipTrack,
      }],
    });
    setSessionDialog(true);
  };

  // QR 코드 다이얼로그 열기
  const openQrDialog = (session: BTSession) => {
    setQrSession(session);
    setQrDialog(true);
  };

  // QR 코드 URL 생성 (교사가 스캔하여 키 입력 페이지로 이동)
  const getQrCodeUrl = (session: BTSession) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/bt/key-input?parentSessionId=${session.sessionId}`;
  };

  const getQrSubSessionLabel = (session: BTSession) => {
    const isBasicTraining = session.trainingCategory === 'basic-training'
      || session.educationProgram?.startsWith('bt-');

    if (isBasicTraining) {
      const club = session.btClubTrack || session.btClubTracks?.[0] || '-';
      const course = getBasicTrainingLevelLabel(session.basicTrainingLevel);
      return `${club} - ${course} - ${session.title}`;
    }

    return `${getLeadershipTrackLabel(session.leadershipTrack || (session.educationProgram as LeadershipTrack))} - ${session.title}`;
  };

  const getQrSubSessions = (session: BTSession) => sessions.filter((candidate) => (
    candidate.eventId === session.eventId
    && candidate.date === session.date
    && candidate.startTime === session.startTime
    && candidate.endTime === session.endTime
    && candidate.location === session.location
    && candidate.trainingCategory === session.trainingCategory
  ));

  // QR 코드 다운로드
  const downloadQrCode = (session: BTSession) => {
    const svg = document.getElementById(`qr-code-${session.sessionId}`);
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `BT-QR-${session.title}-${session.date}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'completed': return 'success';
      case 'active': return 'primary';
      case 'cancelled': return 'error';
      default: return 'warning';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return '승인됨';
      case 'rejected': return '거절됨';
      case 'scheduled': return '예정됨';
      case 'active': return '진행중';
      case 'completed': return '완료됨';
      case 'cancelled': return '취소됨';
      default: return '대기중';
    }
  };

  // 출석 데이터 불러오기 (must be defined before early return for useEffect)
  const btFetchAttendanceData = async () => {
    setBtAttendanceLoading(true);
    try {
      const response = await fetch('/api/bt/bt_attendance');
      const result = await response.json();
      if (result && result.data) {
        setBtAttendanceRecords(result.data);
      }
    } catch (err) {
      console.error("출석 기록 로드 실패:", err);
    } finally {
      setBtAttendanceLoading(false);
    }
  };

  // 페이지 로드 시 출석 데이터 불러오기 (hooks must be declared before any conditional returns)
  useEffect(() => {
    if (role === 'admin') {
      btFetchAttendanceData();
    }
  }, [role]);

  if (role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          이 페이지는 최고 관리자만 접근할 수 있습니다.
        </Alert>
      </Container>
    );
  }

  // --- 클럽 관리 ---
  const btFetchClubs = async () => {
    const res = await fetch('/api/bt/bt_club');
    const data = await res.json();
    setClubs(data);
    console.log("클럽")
    console.log(clubs)
  };

  const handleAddClub = async (clubName: string) => {
    const res = await fetch('/api/bt/bt_club', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ club_name: clubName })
    });
    if (res.ok) btFetchClubs(); // 새로고침
  };

  const handleDeleteClub = async (id: string) => {
    if (!window.confirm('정말 이 클럽을 삭제하시겠습니까?')) return;
    // 요청하신 URL 형식이 3001번 포트의 쿼리 스트링인 경우
    const res = await fetch(`/api/bt/bt_club?id=${id}`, {
      method: 'DELETE'
    });
    if (res.ok) btFetchClubs();
  };

  const handleUpdateClub = async (id: string, currentName: string) => {
    const nextName = window.prompt('수정할 클럽명을 입력하세요:', currentName);
    if (nextName === null) return;

    const trimmed = nextName.trim();
    if (!trimmed || trimmed === currentName) return;

    const res = await fetch('/api/bt/bt_club', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, club_name: trimmed }),
    });

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      setSnackbar({
        open: true,
        message: result?.message || '클럽명 수정에 실패했습니다.',
        severity: 'error',
      });
      return;
    }

    setSnackbar({
      open: true,
      message: '클럽명이 수정되었습니다.',
      severity: 'success',
    });

    btFetchClubs();
    fetchAttendanceData();
    btFetchAttendanceData();
  };

// --- 장소 관리 ---
  const btFetchLocations = async () => {
    const res = await fetch('/api/bt/bt_location');
    const data = await res.json();
    setLocations(data);
    console.log("장소")
    console.log(locations)
  };

  const handleAddLocation = async (locData: { location_name: string; start_time?: string; end_time?: string }) => {
    // locData: { location_name, start_time, end_time }
    const res = await fetch('/api/bt/bt_location', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(locData)
    });
    if (res.ok) btFetchLocations();
  };

  const handleDeleteLocation = async (id: string) => {
    if (!window.confirm('정말 이 장소를 삭제하시겠습니까?')) return;
    const res = await fetch(`/api/bt/bt_location?id=${id}`, {
      method: 'DELETE'
    });
    if (res.ok) btFetchLocations();
  };

  const handleLocationSubmit = async () => {
    try {
      // API 요구 형식에 맞춰 날짜 변환 (ISO String)
      const payload = {
        location_name: newLocation.location_name,
      };

      const res = await fetch('/api/bt/bt_location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('장소가 성공적으로 등록되었습니다.');
        setOpenLocationDialog(false); // 다이얼로그 닫기
        setNewLocation({ location_name: ''}); // 폼 초기화
        btFetchLocations(); // 목록 새로고침 함수 호출
      } else {
        alert('등록에 실패했습니다.');
      }
    } catch (err) {
      console.error(err);
      alert('서버 오류가 발생했습니다.');
    }
  };

// 출석 데이터 삭제 (Query String 방식)
  const btHandleDeleteAttendance = async (id: string) => {
    if (!window.confirm('정말 이 출석 기록을 삭제하시겠습니까?')) return;

    try {
      const res = await fetch(`/api/bt/bt_attendance?id=${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('삭제되었습니다.');
        btFetchAttendanceData(); // 목록 새로고침
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error("삭제 중 오류 발생:", err);
    }
  };

  const filteredSessions = sessions.filter((session) => {
    const inferredCategory: SessionTrainingCategory = session.trainingCategory
      || (session.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');

    if (categoryFilter !== 'all' && inferredCategory !== categoryFilter) {
      return false;
    }

    const hasBasicFilters = basicTrainingTypeFilter !== 'all'
      || basicTrainingLevelFilter !== 'all'
      || clubTrackFilter !== 'all';

    if (hasBasicFilters && inferredCategory !== 'basic-training') {
      return false;
    }

    if (inferredCategory === 'basic-training') {
      const inferredType: BasicTrainingType = session.basicTrainingType || 'regular';
      const inferredLevel: BasicTrainingLevel = session.basicTrainingLevel
        || (session.educationProgram === 'bt-renewal'
          ? 'renewal'
          : session.educationProgram === 'bt-advanced'
            ? 'advanced'
            : 'new');
      const inferredClubs: BTClubTrack[] = session.btClubTracks && session.btClubTracks.length > 0
        ? session.btClubTracks
        : (session.btClubTrack ? [session.btClubTrack] : []);

      if (basicTrainingTypeFilter !== 'all' && inferredType !== basicTrainingTypeFilter) {
        return false;
      }
      if (basicTrainingLevelFilter !== 'all' && inferredLevel !== basicTrainingLevelFilter) {
        return false;
      }
      if (clubTrackFilter !== 'all' && !inferredClubs.includes(clubTrackFilter)) {
        return false;
      }
    }

    return true;
  });

  const getSessionGroupKey = (session: BTSession) => [
    session.eventId,
    session.date,
    session.startTime,
    session.endTime,
    session.location,
    session.trainingCategory,
  ].join('|');

  const openSubLectureDialog = (session: BTSession) => {
    setSelectedParentSession(session);
    setAttendanceSessionId(null);
    setSubLectureAttendance([]);
    setApplicationSessionId(null);
    setSubLectureDialog(true);
  };

  const getLectureApplications = (sessionId: string) => churchManagers.filter((manager) => (
    manager.teachers || []
  ).some((teacher) => !teacher.isDeleted && teacher.lectureSessionId === sessionId));

  const loadSubLectureAttendance = async (session: BTSession) => {
    if (attendanceSessionId === session.sessionId) {
      setAttendanceSessionId(null);
      setSubLectureAttendance([]);
      return;
    }

    setSubLectureAttendanceLoading(true);
    try {
      const records = await btApi.getSessionAttendanceRecords(session.sessionId);
      setAttendanceSessionId(session.sessionId);
      setSubLectureAttendance(records);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '출석 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setSubLectureAttendanceLoading(false);
    }
  };

  const sessionGroups = new Map<string, BTSession[]>();
  filteredSessions.forEach((session) => {
    const groupKey = getSessionGroupKey(session);
    const group = sessionGroups.get(groupKey) || [];
    group.push(session);
    sessionGroups.set(groupKey, group);
  });
  const groupedSessions = Array.from(sessionGroups.values()).map((group) => group[0]);

  const leadershipSessions = sessions.filter((session) => {
    const category = session.trainingCategory
      || (session.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');
    return category === 'leadership-education';
  });

  const displayedAttendanceRecords = attendanceRecords.filter((record) => {
    const isLeadership = getAttendanceTrainingCategory(record) === 'leadership-education';
    if (attendanceTypeFilter === 'basic-training' && isLeadership) return false;
    if (attendanceTypeFilter === 'leadership-education' && !isLeadership) return false;
    if (!leadershipSessionFilter) return true;
    if (!isLeadership) return false;
    return record.sessionId === leadershipSessionFilter || record.session_id === leadershipSessionFilter;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Awana 리더십 교육
        </Typography>
        <Button onClick={() => navigate('/admin')} variant="outlined">
          대시보드로
        </Button>
      </Box>

      {/* 탭 네비게이션 */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="교회 승인 관리" icon={<School />} />
          <Tab label="강의 관리" icon={<Schedule />} />
          <Tab label="출결 관리" icon={<QrCodeScanner />} />
          <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  교사 변경 요청
                  {teacherChangeRequests.length > 0 && (
                      <Chip label={teacherChangeRequests.length} size="small" color="error" />
                  )}
                </Box>
              }
              icon={<People />}
          />
          {/* 추가된 탭 */}
          <Tab label="클럽 관리" icon={<Stars />} />
          <Tab label="장소 관리" icon={<LocationOn />} />
          {/*<Tab label="출석 기록 관리" icon={<AssignmentInd />} />*/}
        </Tabs>
      </Paper>

      {/* 교회 승인 관리 탭 */}
      <TabPanel value={currentTab} index={0}>
        {/* 통계 카드 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>전체 신청</Typography>
                <Typography variant="h4">{churchManagers.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>대기중</Typography>
                <Typography variant="h4">{churchManagers.filter(cm => cm.status === 'pending').length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>승인됨</Typography>
                <Typography variant="h4">{churchManagers.filter(cm => cm.status === 'approved').length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>총 키 발급</Typography>
                <Typography variant="h4">
                  {churchManagers.filter(cm => cm.status === 'approved').reduce((sum, cm) => sum + (cm.participants || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 교회 신청 목록 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>교회명</TableCell>
                  <TableCell>담당자</TableCell>
                  <TableCell>연락처</TableCell>
                  <TableCell>현재 강의</TableCell>
                  <TableCell>교사 강의 배정</TableCell>
                  <TableCell>참가자 수</TableCell>
                  <TableCell>신청일</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {churchManagers.map((manager) => (
                  <TableRow key={manager._id}>
                    <TableCell>
                      <Typography variant="subtitle2">{manager.churchName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {manager.churchAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{manager.managerName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {manager.managerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{manager.managerPhone}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {manager.selectedSessionTitle || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getSessionProgramLabel(manager.selectedSessionProgram)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {(manager.teachers || []).filter((teacher) => !teacher.isDeleted && !!teacher.lectureSessionId).length}/
                        {(manager.teachers || []).filter((teacher) => !teacher.isDeleted).length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        강의 선택 완료/전체 교사
                      </Typography>
                    </TableCell>
                    <TableCell>{manager.participants || '-'}</TableCell>
                    <TableCell>
                      {new Date(manager.registrationDate).toLocaleDateString('ko-KR')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(manager.status)}
                        color={getStatusColor(manager.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {manager.status === 'pending' && (
                          <>
                            <Tooltip title="승인">
                              <IconButton
                                size="small"
                                color="success"
                                aria-label={`${getKoreanChurchName(manager.churchName)} 교회 신청 승인`}
                                onClick={() => openApprovalDialog(manager)}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="거절">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`${getKoreanChurchName(manager.churchName)} 교회 신청 거절`}
                                onClick={() => handleApproval(manager._id, false)}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {manager.status === 'approved' && (
                          <Tooltip title="키 관리">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`${getKoreanChurchName(manager.churchName)} 키 관리`}
                              onClick={() => navigate(`/admin/bt/keys/${manager._id}`)}
                            >
                              <People />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="교회 신청 삭제">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`${getKoreanChurchName(manager.churchName)} 교회 신청 ${deletingChurchManagerId === manager._id ? '삭제 중' : '삭제'}`}
                              aria-busy={deletingChurchManagerId === manager._id}
                              disabled={deletingChurchManagerId !== null}
                              onClick={() => handleDeleteChurchManager(manager)}
                            >
                              {deletingChurchManagerId === manager._id
                                ? <CircularProgress size={18} color="inherit" />
                                : <DeleteIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* 강의 관리 탭 */}
      <TabPanel value={currentTab} index={1}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h6">Awana 강의 관리</Typography>
            <Typography variant="body2" color="text.secondary">
              Basic Training과 리더십 학교 강의를 과정별로 관리합니다.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>대분류</InputLabel>
              <Select
                value={categoryFilter}
                label="대분류"
                onChange={(event) => setCategoryFilter(event.target.value as 'all' | SessionTrainingCategory)}
              >
                <MenuItem value="all">전체</MenuItem>
                {SESSION_TRAINING_CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>교육형태</InputLabel>
              <Select
                value={basicTrainingTypeFilter}
                label="교육형태"
                onChange={(event) => setBasicTrainingTypeFilter(event.target.value as 'all' | BasicTrainingType)}
              >
                <MenuItem value="all">전체</MenuItem>
                {BASIC_TRAINING_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>과정</InputLabel>
              <Select
                value={basicTrainingLevelFilter}
                label="과정"
                onChange={(event) => setBasicTrainingLevelFilter(event.target.value as 'all' | BasicTrainingLevel)}
              >
                <MenuItem value="all">전체</MenuItem>
                {BASIC_TRAINING_LEVEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>소속</InputLabel>
              <Select
                value={clubTrackFilter}
                label="소속"
                onChange={(event) => setClubTrackFilter(event.target.value as 'all' | BTClubTrack)}
              >
                <MenuItem value="all">전체</MenuItem>
                {BT_CLUB_TRACK_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingSession(null);
              setCopyingSession(false);
              resetSessionForm();
              setSessionDialog(true);
            }}
          >
            새 강의 생성
          </Button>
          </Stack>
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>상위 강의</TableCell>
                  <TableCell>날짜/시간</TableCell>
                  <TableCell>장소</TableCell>
                  <TableCell>참가자</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">선택한 과정에 등록된 강의가 없습니다.</Typography>
                    </TableCell>
                  </TableRow>
                ) : groupedSessions.map((session) => (
                  <TableRow
                    key={session._id}
                    hover
                    onClick={() => openSubLectureDialog(session)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {(() => {
                        const inferredCategory: SessionTrainingCategory = session.trainingCategory
                          || (session.educationProgram?.startsWith('bt-') ? 'basic-training' : 'leadership-education');
                        const isBasic = inferredCategory === 'basic-training';

                        return (
                          <>
                            <Chip
                              label={getTrainingCategoryLabel(inferredCategory)}
                              size="small"
                              variant="outlined"
                              color={isBasic ? 'primary' : 'secondary'}
                            />
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }} color="text.secondary">
                              {isBasic
                                ? `${getBasicTrainingTypeLabel(session.basicTrainingType)} · ${getBasicTrainingLevelLabel(session.basicTrainingLevel)} · ${((session.btClubTracks && session.btClubTracks.length > 0) ? session.btClubTracks.join(', ') : (session.btClubTrack || '-'))}`
                                : getLeadershipTrackLabel(session.leadershipTrack || (session.educationProgram as LeadershipTrack))}
                            </Typography>
                          </>
                        );
                      })()}
                      <Typography
                        variant="subtitle2"
                        color="primary"
                      >
                        {session.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {session.description}
                      </Typography>
                      {sessionGroups.get(getSessionGroupKey(session))!.length > 1 && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 600 }}>
                          하위 강의 {sessionGroups.get(getSessionGroupKey(session))!.length}개 포함
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(session.date).toLocaleDateString('ko-KR')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {session.startTime} - {session.endTime}
                      </Typography>
                    </TableCell>
                    <TableCell>{session.location}</TableCell>
                    <TableCell>
                      {(session.uniqueGroupParticipants ?? session.currentParticipants)} / {session.maxParticipants}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(session.status)}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="QR 코드 보기">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => openQrDialog(session)}
                          >
                            <QrCode2 />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="수정">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditSession(session)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="새 상위 강의로 하위 강의 전체 복사">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleCopySession(session)}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSessionGroup(session)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="출결 관리">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => navigate(`/admin/bt/attendance/${session.sessionId}`)}
                          >
                            <QrCodeScanner />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* 출결 관리 탭 (Index 2) */}
      <TabPanel value={currentTab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>BT · 리더십 학교 출석 기록</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
                variant="contained"
                color="success"
                startIcon={<Download />}
                onClick={() => {
                  const exportData = displayedAttendanceRecords.map((record) => {
                    const isLeadership = getAttendanceTrainingCategory(record) === 'leadership-education';
                    return {
                      '교사명': record.name || (typeof record.teacherId === 'object' ? record.teacherId.teacherName : '') || '-',
                      '연락처': record.teacher_phone || (typeof record.teacherId === 'object' ? record.teacherId.teacherPhone : '') || '-',
                      '교회명': record.church_name || (typeof record.teacherId === 'object' ? record.teacherId.churchName : '') || '-',
                      '교회번호': record.church_id || '-',
                      '출석 유형': isLeadership ? '리더십 학교' : 'Basic Training',
                      'BT 클럽 / 리더십 과정': isLeadership
                        ? getLeadershipTrackLabel(record.session?.leadershipTrack || record.session?.educationProgram as LeadershipTrack)
                        : getAttendanceBasicProgramLabel(record, clubs),
                      '강의명': record.session?.title || '-',
                      '장소': getAttendanceLocationLabel(record),
                      '등록일': new Date(record.checkInTime || record.createdAt).toLocaleDateString('ko-KR'),
                    };
                  });
                  const ws = XLSX.utils.json_to_sheet(exportData);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'BT출석기록');
                  XLSX.writeFile(wb, `BT출석기록_${new Date().toISOString().split('T')[0]}.xlsx`);
                }}
                disabled={displayedAttendanceRecords.length === 0}
            >
              엑셀 다운로드
            </Button>
            <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  fetchAttendanceFilterLocations();
                  fetchAttendanceData(1);
                }}
                disabled={attendanceLoading}
            >
              새로고침
            </Button>
          </Box>
        </Box>

        {/* 필터 UI - Index 6의 여백 및 스타일 적용 */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>출석 유형</InputLabel>
                <Select
                  value={attendanceTypeFilter}
                  label="출석 유형"
                  onChange={(e) => {
                    const value = e.target.value as 'all' | 'basic-training' | 'leadership-education';
                    setAttendanceTypeFilter(value);
                    setLeadershipSessionFilter('');
                  }}
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="basic-training">Basic Training</MenuItem>
                  <MenuItem value="leadership-education">리더십 학교</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>수강지역</InputLabel>
                <Select
                  value={filterLocation}
                  label="수강지역"
                  disabled={attendanceTypeFilter === 'leadership-education'}
                  onChange={(e) => {
                    const selectedValue = e.target.value;

                    // 콘솔에 로그 찍기
                    console.log("선택된 수강지역 값:", selectedValue);

                    // 상태 업데이트
                    setFilterLocation(selectedValue);
                  }}
                >
                  <MenuItem value="">전체</MenuItem>
                  {attendanceFilterLocations.map((loc) => (
                    <MenuItem key={loc._id} value={loc._id}>
                      {loc.location_name}{loc.isDeleted ? ' (삭제됨)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>클럽</InputLabel>
                <Select
                  value={filterClub}
                  label="클럽"
                  disabled={attendanceTypeFilter === 'leadership-education'}
                  onChange={(e) => {
                    const selectedClubId = e.target.value;

                    console.log("선택된 클럽 ID:", selectedClubId);

                    setFilterClub(selectedClubId);
                  }}                >
                  <MenuItem value="">전체</MenuItem>
                  {clubs.map((club) => (
                    <MenuItem key={club._id} value={club._id}>
                      {getClubLabel(club)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>리더십 강의</InputLabel>
                <Select
                  value={leadershipSessionFilter}
                  label="리더십 강의"
                  disabled={attendanceTypeFilter === 'basic-training'}
                  onChange={(e) => setLeadershipSessionFilter(e.target.value)}
                >
                  <MenuItem value="">전체</MenuItem>
                  {leadershipSessions.map((session) => (
                    <MenuItem key={session.sessionId} value={session.sessionId}>{session.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                총 {attendanceTotalCount}건의 기록
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* 출석 테이블 - Index 6과 동일한 스티키 헤더 및 디자인 */}
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <TableContainer sx={{ maxHeight: 640 }} onScroll={handleAttendanceTableScroll}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>교사명</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>연락처</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>교회명</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>출석 유형</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>클럽 / 리더십 과정</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>강의 / 장소</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>등록일</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }} align="center">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={30} sx={{ mb: 2 }} />
                        <Typography color="text.secondary">데이터를 불러오는 중입니다...</Typography>
                      </TableCell>
                    </TableRow>
                ) : displayedAttendanceRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Typography color="text.secondary">출석 기록이 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                ) : (
                    displayedAttendanceRecords.map((record) => {
                      const isLeadership = getAttendanceTrainingCategory(record) === 'leadership-education';
                      const teacher = typeof record.teacherId === 'object' ? record.teacherId : undefined;
                      return (
                        <TableRow key={record._id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{record.name || teacher?.teacherName || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.teacher_phone || teacher?.teacherPhone || '-'}</TableCell>
                          <TableCell>{getKoreanChurchName(record.church_name || teacher?.churchName)}</TableCell>
                          <TableCell>
                            <Chip
                                label={isLeadership ? '리더십 학교' : 'Basic Training'}
                                size="small"
                                color={isLeadership ? 'secondary' : 'primary'}
                                variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                                label={isLeadership
                                  ? getLeadershipTrackLabel(record.session?.leadershipTrack || record.session?.educationProgram as LeadershipTrack)
                                  : getAttendanceBasicProgramLabel(record, clubs)}
                                color={isLeadership ? 'secondary' : 'primary'}
                                size="small"
                                variant="filled"
                                sx={{ borderRadius: '4px', fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{record.session?.title || '-'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getAttendanceLocationLabel(record)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(record.checkInTime || record.createdAt).toLocaleDateString('ko-KR')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(record.checkInTime || record.createdAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <Tooltip title="수정">
                                <IconButton
                                  aria-label={`출석 기록 수정: ${record.name || teacher?.teacherName || '교사'}`}
                                  color="primary"
                                  onClick={() => setEditingAttendance(record)}
                                  sx={{ minWidth: 44, minHeight: 44, '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton
                                  aria-label={`출석 기록 삭제: ${record.name || teacher?.teacherName || '교사'}`}
                                  color="error"
                                  onClick={() => handleDeleteAttendance(record._id)}
                                  sx={{ minWidth: 44, minHeight: 44, '&:hover': { bgcolor: '#fff5f5' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
                {attendanceLoadingMore && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, bgcolor: '#fdfdfd', borderTop: '1px solid #eee', textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              현재 필터 기준 {attendanceRecords.length}건이 표시됨
            </Typography>
          </Box>
        </Paper>

        <BTAttendanceEditDialog
          key={editingAttendance?._id || 'closed-attendance-edit'}
          record={editingAttendance}
          locations={attendanceFilterLocations}
          clubs={clubs}
          fallbackLocationId={filterLocation.startsWith('deleted-name:') ? '' : filterLocation}
          isLeadership={editingAttendance
            ? getAttendanceTrainingCategory(editingAttendance) === 'leadership-education'
            : false}
          onClose={() => setEditingAttendance(null)}
          onSave={handleUpdateAttendance}
        />
      </TabPanel>

      {/* 교사 변경 요청 관리 탭 */}
      <TabPanel value={currentTab} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">교사 변경 요청</Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTeacherChangeRequests}
            disabled={changeRequestLoading}
          >
            새로고침
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>교회명</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>요청 유형</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>교사 정보</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>요청일</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changeRequestLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      데이터를 불러오는 중...
                    </TableCell>
                  </TableRow>
                ) : teacherChangeRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      대기 중인 변경 요청이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  teacherChangeRequests.map((request) => (
                    <TableRow key={request._id}>
                      <TableCell>
                        {typeof request.churchManagerId === 'object'
                          ? request.churchManagerId.churchName
                          : request.churchManagerId}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.requestType === 'add' ? '추가' : request.requestType === 'delete' ? '삭제' : '수정'}
                          color={request.requestType === 'add' ? 'success' : request.requestType === 'delete' ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.teacherData.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.teacherData.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(request.requestDate).toLocaleDateString('ko-KR')}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="승인">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveRequest(request._id)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="거절">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedRequest(request);
                                setRejectDialog(true);
                              }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* 클럽 관리 탭 (Index 4) */}
      <TabPanel value={currentTab} index={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">BT 클럽 목록</Typography>
          <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // 클럽 추가 다이얼로그 오픈 로직 (예시)
                const name = prompt('추가할 클럽명을 입력하세요:');
                if (name) handleAddClub(name);
              }}
          >
            새 클럽 추가
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>클럽명</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clubs.map((club: any) => (
                    <TableRow key={club._id}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{club.club_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="수정">
                          <IconButton color="primary" onClick={() => handleUpdateClub(club._id, club.club_name)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton color="error" onClick={() => handleDeleteClub(club._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* 장소 관리 탭 (Index 5) */}
      <TabPanel value={currentTab} index={5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">수강 장소 목록</Typography>
          <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // 장소 추가 다이얼로그 호출 로직 필요
                setOpenLocationDialog(true);
              }}
          >
            새 장소 추가
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>장소명</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((loc) => (
                    <TableRow key={loc._id}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{loc.location_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="삭제">
                          <IconButton color="error" onClick={() => handleDeleteLocation(loc._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* 출석 관리 탭 (Index 6) */}
      <TabPanel value={currentTab} index={6}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">BT · 리더십 학교 출석 현황 관리</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={btFetchAttendanceData}
                disabled={btAttendanceLoading}
            >
              새로고침
            </Button>
          </Box>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>교사명</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>연락처</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>소속 교회</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>교육 구분</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>클럽</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>수강 지역</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }}>출석 시간</TableCell>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 600 }} align="center">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {btAttendanceLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={30} sx={{ mb: 2 }} />
                        <Typography color="text.secondary">데이터를 불러오는 중입니다...</Typography>
                      </TableCell>
                    </TableRow>
                ) : btAttendanceRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                        <Typography color="text.secondary">조회된 출석 기록이 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                ) : (
                    btAttendanceRecords.map((record: any) => {
                        const isLeadership = getAttendanceTrainingCategory(record) === 'leadership-education';
                        return (
                        <TableRow key={record._id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{record.name || record.teacherId?.teacherName || '-'}</TableCell>
                          <TableCell>{record.teacher_phone || record.teacherId?.teacherPhone || '-'}</TableCell>
                          <TableCell>{record.church_name || record.teacherId?.churchName || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={isLeadership ? '리더십 학교' : 'Basic Training'}
                              size="small"
                              color={isLeadership ? 'secondary' : 'primary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getAttendanceBasicProgramLabel(record, clubs) || (record.sessionType || '-')}
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: '4px', fontWeight: 600, color: '#4c63d2', borderColor: '#4c63d2' }}
                            />
                          </TableCell>
                          <TableCell>{record.bt_location_name || record.location || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(record.checkInTime || record.createdAt).toLocaleDateString('ko-KR')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(record.checkInTime || record.createdAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="기록 삭제">
                              <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => btHandleDeleteAttendance(record._id)}
                                  sx={{ '&:hover': { bgcolor: '#fff5f5' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 하단 요약 정보 (선택 사항) */}
          <Box sx={{ p: 2, bgcolor: '#fdfdfd', borderTop: '1px solid #eee', textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              총 {btAttendanceRecords.length}개의 출석 기록이 조회되었습니다.
            </Typography>
          </Box>
        </Paper>
      </TabPanel>

      {/* 장소 추가 다이얼로그 */}
      <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>새 수강 장소 추가</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
                fullWidth
                label="장소명"
                placeholder="예: 서울, 금광교회"
                value={newLocation.location_name}
                onChange={(e) => setNewLocation({ ...newLocation, location_name: e.target.value })}
            />
            {/*<TextField*/}
            {/*    fullWidth*/}
            {/*    label="시작 날짜"*/}
            {/*    type="date"*/}
            {/*    InputLabelProps={{ shrink: true }}*/}
            {/*    value={newLocation.start_time}*/}
            {/*    onChange={(e) => setNewLocation({ ...newLocation, start_time: e.target.value })}*/}
            {/*/>*/}
            {/*<TextField*/}
            {/*    fullWidth*/}
            {/*    label="종료 날짜"*/}
            {/*    type="date"*/}
            {/*    InputLabelProps={{ shrink: true }}*/}
            {/*    value={newLocation.end_time}*/}
            {/*    onChange={(e) => setNewLocation({ ...newLocation, end_time: e.target.value })}*/}
            {/*/>*/}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenLocationDialog(false)} color="inherit">취소</Button>
          <Button
              variant="contained"
              onClick={handleLocationSubmit}
              disabled={!newLocation.location_name}
          >
            등록하기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 거절 사유 다이얼로그 */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>교사 변경 요청 거절</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {selectedRequest && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedRequest.requestType === 'add' ? '추가' : '삭제'} 요청: {selectedRequest.teacherData.name} ({selectedRequest.teacherData.phone})
              </Alert>
            )}
            <TextField
              fullWidth
              label="거절 사유"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={3}
              placeholder="거절 사유를 입력해주세요 (선택)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>취소</Button>
          <Button variant="contained" color="error" onClick={handleRejectRequest}>
            거절
          </Button>
        </DialogActions>
      </Dialog>

      {/* 승인 다이얼로그 */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>BT 신청 승인</DialogTitle>
        <DialogContent>
          {selectedManager && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedManager.churchName} - {selectedManager.managerName}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>이벤트</InputLabel>
                <Select
                  value={approvalData.eventId}
                  label="이벤트"
                  onChange={(e) => setApprovalData({...approvalData, eventId: e.target.value})}
                >
                  {BT_EVENT_OPTIONS.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="참가비 (원)"
                type="number"
                value={approvalData.costs}
                onChange={(e) => setApprovalData({...approvalData, costs: e.target.value})}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="참가 교사 수"
                type="number"
                value={approvalData.partTeacher}
                onChange={(e) => setApprovalData({...approvalData, partTeacher: e.target.value})}
                sx={{ mb: 2 }}
              />
              
              <Alert severity="info" sx={{ mt: 2 }}>
                승인 시 자동으로 키가 생성되고 영수증이 발급됩니다.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => selectedManager && handleApproval(selectedManager._id, true)}
            disabled={!approvalData.eventId || !approvalData.costs || !approvalData.partTeacher}
          >
            승인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상위 강의의 하위 강의 목록 */}
      <Dialog
        open={subLectureDialog}
        onClose={() => setSubLectureDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <span>하위 강의 목록</span>
            {selectedParentSession && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddSubLecture(selectedParentSession)}
              >
                하위 강의 추가
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedParentSession && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedParentSession.eventId} · {new Date(selectedParentSession.date).toLocaleDateString('ko-KR')} · {selectedParentSession.location}
              </Typography>
              <Stack spacing={1.5}>
                {sessions
                  .filter((session) => getSessionGroupKey(session) === getSessionGroupKey(selectedParentSession))
                  .map((session, index) => (
                    <Paper key={session._id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            강의 {index + 1}: {getQrSubSessionLabel(session)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {session.description || '설명이 없습니다.'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Tooltip title="QR 코드 보기">
                            <IconButton size="small" color="success" onClick={() => {
                              setSubLectureDialog(false);
                              openQrDialog(session);
                            }}>
                              <QrCode2 fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="수정">
                            <IconButton size="small" color="primary" onClick={() => {
                              setSubLectureDialog(false);
                              handleEditSession(session);
                            }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="삭제">
                            <IconButton size="small" color="error" onClick={() => handleDeleteSession(session._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => loadSubLectureAttendance(session)}
                          >
                            출석 {session.currentParticipants || 0}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setApplicationSessionId(
                              applicationSessionId === session.sessionId ? null : session.sessionId
                            )}
                          >
                            신청 {getLectureApplications(session.sessionId).length}
                          </Button>
                        </Box>
                      </Box>
                      {attendanceSessionId === session.sessionId && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          {subLectureAttendanceLoading ? (
                            <Typography variant="body2" color="text.secondary">출석 목록을 불러오는 중입니다.</Typography>
                          ) : subLectureAttendance.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">출석 기록이 없습니다.</Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary">
                                출석 {subLectureAttendance.length}명
                              </Typography>
                              {subLectureAttendance.map((record) => (
                                <Typography key={record._id} variant="body2">
                                  {record.teacherId?.teacherName || record.name || '이름 없음'}
                                  {' · '}
                                  {record.checkInTime
                                    ? new Date(record.checkInTime).toLocaleString('ko-KR')
                                    : '시간 없음'}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}
                      {applicationSessionId === session.sessionId && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          {getLectureApplications(session.sessionId).length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              이 하위 강의에 신청한 교회가 없습니다.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                신청 교회 {getLectureApplications(session.sessionId).length}곳
                              </Typography>
                              {getLectureApplications(session.sessionId).map((manager) => (
                                <Box key={manager._id} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {manager.churchName} · {manager.managerName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {manager.managerPhone}
                                  </Typography>
                                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                    {(manager.teachers || [])
                                      .filter((teacher) => !teacher.isDeleted && teacher.lectureSessionId === session.sessionId)
                                      .map((teacher) => (
                                        <Typography key={`${manager._id}-${teacher.id}`} variant="body2">
                                          {teacher.name} · {teacher.phone} · {teacher.status || '신청'}
                                        </Typography>
                                      ))}
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubLectureDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 강의 생성/수정 다이얼로그 */}
      <Dialog open={sessionDialog} onClose={() => setSessionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSession ? '강의 수정' : copyingSession ? '새 상위 강의로 복사' : '새 강의 생성'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>공통 설정</Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>대분류</InputLabel>
              <Select
                value={sessionFormData.trainingCategory}
                label="대분류"
                onChange={(e) => setSessionFormData({
                  ...sessionFormData,
                  trainingCategory: e.target.value as SessionTrainingCategory,
                })}
              >
                {SESSION_TRAINING_CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {sessionFormData.trainingCategory === 'basic-training' ? (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>교육 형태</InputLabel>
                <Select
                  value={sessionFormData.basicTrainingType}
                  label="교육 형태"
                  onChange={(e) => setSessionFormData({
                    ...sessionFormData,
                    basicTrainingType: e.target.value as BasicTrainingType,
                  })}
                >
                  {BASIC_TRAINING_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>교육 형태</InputLabel>
                <Select
                  value="leadership-education"
                  label="교육 형태"
                  disabled
                >
                  <MenuItem value="leadership-education">리더십교육</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="신청 시작일"
                type="date"
                value={sessionFormData.registrationStartDate}
                onChange={(e) => setSessionFormData({...sessionFormData, registrationStartDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="신청 종료일"
                type="date"
                value={sessionFormData.registrationEndDate}
                onChange={(e) => setSessionFormData({...sessionFormData, registrationEndDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>세션 타입</InputLabel>
              <Select
                value={sessionFormData.sessionType}
                onChange={(e) => setSessionFormData({...sessionFormData, sessionType: e.target.value as any})}
              >
                <MenuItem value="morning">오전</MenuItem>
                <MenuItem value="afternoon">오후</MenuItem>
                <MenuItem value="evening">저녁</MenuItem>
                <MenuItem value="full-day">종일</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="날짜"
              type="date"
              value={sessionFormData.date}
              onChange={(e) => setSessionFormData({...sessionFormData, date: e.target.value})}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="시작 시간"
                type="time"
                value={sessionFormData.startTime}
                onChange={(e) => setSessionFormData({...sessionFormData, startTime: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="종료 시간"
                type="time"
                value={sessionFormData.endTime}
                onChange={(e) => setSessionFormData({...sessionFormData, endTime: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            
            <TextField
              fullWidth
              label="장소"
              value={sessionFormData.location}
              onChange={(e) => setSessionFormData({...sessionFormData, location: e.target.value})}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="최대 참가자 수"
              type="number"
              value={sessionFormData.maxParticipants}
              onChange={(e) => setSessionFormData({...sessionFormData, maxParticipants: parseInt(e.target.value)})}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>이벤트</InputLabel>
              <Select
                value={sessionFormData.eventId}
                label="이벤트"
                onChange={(e) => setSessionFormData({...sessionFormData, eventId: e.target.value})}
              >
                {BT_EVENT_OPTIONS.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>하위 강의</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addLectureItem}>강의 추가</Button>
              </Stack>

              {sessionFormData.lectures.map((lecture, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>강의 {index + 1}</Typography>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => removeLectureItem(index)}
                      disabled={sessionFormData.lectures.length === 1}
                    >
                      삭제
                    </Button>
                  </Stack>

                  {sessionFormData.trainingCategory === 'basic-training' ? (
                    <>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>과정</InputLabel>
                        <Select
                          value={lecture.basicTrainingLevel}
                          label="과정"
                          onChange={(e) => updateLectureItem(index, 'basicTrainingLevel', e.target.value as BasicTrainingLevel)}
                        >
                          {BASIC_TRAINING_LEVEL_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>소속</InputLabel>
                        <Select
                          value={lecture.btClubTracks?.[0] || lecture.btClubTrack || ''}
                          label="소속"
                          onChange={(e) => {
                            const selectedTrack = e.target.value as BTClubTrack;
                            updateLectureItem(index, 'btClubTracks', [selectedTrack]);
                          }}
                        >
                          {BT_CLUB_TRACK_OPTIONS.map((option) => {
                            return (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          강의 1개당 소속 1개만 선택할 수 있습니다.
                        </Typography>
                      </FormControl>
                    </>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>리더십교육 과정</InputLabel>
                      <Select
                        value={lecture.leadershipTrack}
                        label="리더십교육 과정"
                        onChange={(e) => updateLectureItem(index, 'leadershipTrack', e.target.value as LeadershipTrack)}
                      >
                        {LEADERSHIP_TRACK_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    label="강의명"
                    value={lecture.title}
                    onChange={(e) => updateLectureItem(index, 'title', e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="강의 설명"
                    value={lecture.description}
                    onChange={(e) => updateLectureItem(index, 'description', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Paper>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionDialog(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleSessionSubmit}
            disabled={
              !sessionFormData.date ||
              !sessionFormData.location ||
              !sessionFormData.eventId ||
              sessionFormData.lectures.length === 0 ||
              sessionFormData.lectures.some((lecture) => !lecture.title.trim()) ||
              (sessionFormData.trainingCategory === 'basic-training'
                ? (!sessionFormData.basicTrainingType || sessionFormData.lectures.some((lecture) => !(lecture.btClubTracks && lecture.btClubTracks.length > 0)))
                : sessionFormData.lectures.some((lecture) => !lecture.leadershipTrack))
            }
          >
            {editingSession ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR 코드 다이얼로그 */}
      <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>상위 강의 QR 코드</span>
            {qrSession && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Download />}
                onClick={() => downloadQrCode(qrSession)}
              >
                다운로드
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {qrSession && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              {getQrSubSessions(qrSession).length > 1 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  이 상위 강의에는 하위 강의 {getQrSubSessions(qrSession).length}개가 포함되어 있습니다.
                </Typography>
              )}
              <Typography variant="h6" gutterBottom>
                {qrSession.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {new Date(qrSession.date).toLocaleDateString('ko-KR')} | {qrSession.startTime} - {qrSession.endTime}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {qrSession.location}
              </Typography>

              <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
                <Paper elevation={3} sx={{ p: 3, bgcolor: 'white' }}>
                  <QRCodeSVG
                    id={`qr-code-${qrSession.sessionId}`}
                    value={getQrCodeUrl(qrSession)}
                    size={250}
                    level="H"
                    includeMargin={true}
                  />
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>교사용 안내:</strong><br />
                  1. 이 QR 코드를 스캔하세요<br />
                  2. 교회를 선택한 뒤 기존 교사를 선택하세요<br />
                  3. 클럽, 과정(신규/재교육/심화), 과목을 선택하세요<br />
                  4. 교사가 없으면 교사를 생성한 뒤 출석 체크를 진행하세요
                </Typography>
              </Alert>

              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  QR 링크: {getQrCodeUrl(qrSession)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BTAdminPage;
